import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { useAuth } from '../contexts/AuthContext';
import HelpAskPanel from '../components/help/HelpAskPanel';
import HelpArticleList from '../components/help/HelpArticleList';
import HelpSubnav from '../components/help/HelpSubnav';
import { GET_HELP_ARTICLES } from '../graphql/helpContent';
import { GET_HELP_TUTORIALS, GET_HELP_TUTORIALS_FOR_USER, GET_HELP_TUTORIAL, GET_HELP_TUTORIAL_FOR_USER } from '../graphql/helpTutorials';

export default function HelpTutorials() {
  const { user, isLoggingOut } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const [selectedTutorialId, setSelectedTutorialId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data, loading, error } = useQuery(GET_HELP_ARTICLES, {
    variables: { kind: 'TUTORIAL' },
  });

  const { data: videoData, loading: videoLoading } = useQuery(
    isAdmin ? GET_HELP_TUTORIALS : GET_HELP_TUTORIALS_FOR_USER,
    { skip: !user || isLoggingOut, errorPolicy: 'all' },
  );

  const { data: tutorialData, loading: tutorialLoading, error: tutorialError } = useQuery(
    isAdmin ? GET_HELP_TUTORIAL : GET_HELP_TUTORIAL_FOR_USER,
    {
      variables: { id: selectedTutorialId },
      skip: !selectedTutorialId || !isModalOpen || !user || isLoggingOut,
      errorPolicy: 'all',
    },
  );

  const videoTutorials = isAdmin
    ? (videoData?.helpTutorials || [])
    : (videoData?.helpTutorialsForUser || []);
  const selectedTutorial = isAdmin
    ? tutorialData?.helpTutorial
    : tutorialData?.helpTutorialForUser;

  const handleTutorialClick = (tutorialId) => {
    setSelectedTutorialId(parseInt(tutorialId, 10));
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTutorialId(null);
  };

  const handleTutorialKeyDown = (event, tutorialId) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleTutorialClick(tutorialId);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white lg:text-gray-900">Tutorials</h1>
          <p className="mt-1 text-sm text-sky-100 lg:text-gray-600">
            Step-by-step guides for your role, plus any video tutorials assigned to you.
          </p>
        </div>
        <HelpSubnav />
      </div>
      <HelpAskPanel />
      <HelpArticleList
        articles={data?.helpArticles || []}
        loading={loading}
        error={error}
        emptyMessage="No written tutorials are available for your role yet."
      />

      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-white lg:text-gray-500">
          Video tutorials
        </h2>
        {videoLoading ? (
          <div className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-600">
            Loading video tutorials…
          </div>
        ) : videoTutorials.length === 0 ? (
          <div className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-600">
            No video tutorials are available for your role.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {videoTutorials.map((tutorial) => (
              <div
                key={tutorial.id}
                role="button"
                tabIndex={0}
                aria-label={`Open video tutorial ${tutorial.title}`}
                onClick={() => handleTutorialClick(tutorial.id)}
                onKeyDown={(event) => handleTutorialKeyDown(event, tutorial.id)}
                className="cursor-pointer rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition hover:border-purple-300 hover:bg-purple-50"
              >
                <h3 className="text-base font-semibold text-gray-900">{tutorial.title}</h3>
                <p className="mt-1 text-sm text-gray-500">Video or rich-text tutorial</p>
              </div>
            ))}
          </div>
        )}
      </section>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-lg bg-white">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h2 className="text-xl font-semibold text-gray-900">
                {tutorialLoading ? 'Loading...' : selectedTutorial?.title || 'Tutorial'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-500"
                aria-label="Close tutorial"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              {tutorialLoading ? (
                <p className="text-gray-600">Loading tutorial...</p>
              ) : tutorialError || !selectedTutorial ? (
                <p className="text-red-600">This tutorial is not accessible with your current role.</p>
              ) : (
                <>
                  <div className="prose mb-6 max-w-none" dangerouslySetInnerHTML={{ __html: selectedTutorial.content }} />
                  {selectedTutorial.videoUrl && (
                    <div className="mt-6 overflow-hidden rounded-md border border-gray-200">
                      {/\.(jpg|jpeg|png|gif|webp)$/i.test(selectedTutorial.videoUrl) ? (
                        <img src={selectedTutorial.videoUrl} alt="Tutorial media" className="h-auto max-h-96 w-full object-contain" />
                      ) : (
                        <video src={selectedTutorial.videoUrl} controls className="h-auto max-h-96 w-full" />
                      )}
                    </div>
                  )}
                  <div className="mt-6 flex justify-end border-t border-gray-200 pt-4">
                    <button
                      onClick={handleCloseModal}
                      className="rounded-md bg-purple-600 px-4 py-2 text-white hover:bg-purple-700"
                    >
                      Close
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
